import type { Database } from 'node-sqlite3-wasm';
import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import { withCodeNavCacheSqlite, withCodeNavCacheSqliteProbe } from '../../../sqlite/code-nav-cache/database.js';
import { getSymbolIndexParentContext, SYMBOL_INDEX_VERSION } from '../sqlite/symbol-index-sqlite.js';
import { getEffectiveContentHash, listEffectiveIndexedFilePaths } from '../storage/symbol-parent-file-map.js';

function symbolIndexFileIsReadyWithDb(db: Database, parentPath: string, filePath: string): boolean {
  const normalizedParent = normalizeResolvedPath(parentPath);
  const normalizedFile = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
  if (!normalizedFile) return false;

  const ctx = getSymbolIndexParentContext(db, normalizedParent);
  if (ctx == null) return false;

  const versionRow = db.get('SELECT index_version FROM symbol_index_parent_path WHERE id = ?', [
    ctx.parentId,
  ]) as { index_version: number } | null;
  if (versionRow == null || versionRow.index_version < SYMBOL_INDEX_VERSION) return false;

  return getEffectiveContentHash(db, ctx, normalizedFile) != null;
}

/** True when the effective file map resolves to indexed content for this parent + path. */
export async function symbolIndexFileIsReadyAsync(parentPath: string, filePath: string): Promise<boolean> {
  return withCodeNavCacheSqlite((db) => symbolIndexFileIsReadyWithDb(db, parentPath, filePath));
}

/** Non-blocking readiness probe for interactive code-nav (no long SQLite lock retries). */
export async function symbolIndexFileIsReadyProbe(parentPath: string, filePath: string): Promise<boolean> {
  const ready = await withCodeNavCacheSqliteProbe((db) =>
    symbolIndexFileIsReadyWithDb(db, parentPath, filePath),
  );
  return ready === true;
}

/** All indexed relative paths for a parent (one SQLite round-trip for warmup scheduling). */
export async function loadSymbolIndexReadyFileSet(parentPath: string): Promise<Set<string>> {
  const normalizedParent = normalizeResolvedPath(parentPath);
  const paths = await withCodeNavCacheSqlite((db) => {
    const ctx = getSymbolIndexParentContext(db, normalizedParent);
    if (ctx == null) return [] as string[];

    const versionRow = db.get('SELECT index_version FROM symbol_index_parent_path WHERE id = ?', [
      ctx.parentId,
    ]) as { index_version: number } | null;
    if (versionRow == null || versionRow.index_version < SYMBOL_INDEX_VERSION) return [] as string[];

    return listEffectiveIndexedFilePaths(db, ctx);
  });
  return new Set(paths);
}
